/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import AssertionError from 'assertion-error';
import { BaseContentController } from './base-content-controller';

/**
 * @import { ContentController, ContentControllerType, ContentControllerFactory } from '.'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { Content } from '../content'
 * @import { Layout } from '../../layout/layout'
 */

/**
 * @implements {ContentController}
 */
export class LayoutContentController extends BaseContentController {
  /**
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {Content} content
   * @param {HTMLElement} parentEl
   * @param {number} parentVolume
   */
  constructor(
    contentControllerFactory,
    contentInfo,
    content,
    parentEl,
    parentVolume
  ) {
    super();
    this.contentControllerFactory = contentControllerFactory;
    this.parentEl = parentEl;
    this.contentInfo = contentInfo;
    this.content = content;
    this.parentVolume = parentVolume;

    if (!content.layout) {
      throw new AssertionError('content is not layout');
    }

    /** @type {ContentControllerType} */
    this.type = 'layout';

    /** @type {Layout} */
    this.layout = content.layout;
    /** @type {ContentController[]} */
    this.contentControllers = [];

    /** @type {HTMLDivElement | undefined} */
    this.layoutEl = undefined;
    /** @type {HTMLDivElement[]} */
    this.layerEls = [];

    this.onPlayed = this.onPlayed.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
  }

  /**
   * @param {Content} content
   */
  onPlayed(content) {
    this.emitter.emit('played', content);
  }

  /**
   * @param {Content} content
   */
  onPlaying(content) {
    this.emitter.emit('playing', content);
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      /** @type {ContentController[]} */
      const contentControllers = [];
      const { layers } = this.layout;

      const layoutEl = document.createElement('div');
      Object.assign(layoutEl.style, {
        backgroundColor: this.layout.backgroundColor || '#000000',
        height: '100%',
        position: 'absolute',
        width: '100%',
        zIndex: '0',
        visibility: 'hidden'
      });

      const layerEls = layers.map((l) => {
        const el = document.createElement('div');
        Object.assign(el.style, {
          position: 'absolute',
          width: l.width,
          height: l.height,
          top: l.top,
          left: l.left,
          // Přičítání 2 k indexu kvůli tomu, a že top level element má z-index 0 a za serveru
          // pozadí chodí typicky s indexem -1, tak aby byl nejnižší index ze serveru 1.
          // Pokud by vrstva měla index menší než je index rodičovského elementu, tak se vrstva pod něj schová.
          zIndex: parseInt(l.zIndex, 10) + 2
        });
        layoutEl.appendChild(el);

        const contentController = this.contentControllerFactory.createForRegion(
          el,
          l,
          { contentInfo: this.contentInfo, volume: 1 }
        );
        contentControllers.push(contentController);
        return el;
      });
      this.parentEl.appendChild(layoutEl);

      await callBulkContentControllerMethod(
        contentControllers,
        (contentController) => {
          contentController.on('played', this.onPlayed);
          contentController.on('playing', this.onPlaying);
          return contentController.preload();
        }
      );

      this.contentControllers = contentControllers;
      this.layoutEl = layoutEl;
      this.layerEls = layerEls;
    });
  }

  /** @type {ContentController['restart']} */
  async restart() {
    await super.restartCallback(async () => {
      const { contentControllers } = this;
      await callBulkContentControllerMethod(
        contentControllers,
        (contentController) => contentController.restart()
      );
    });
  }

  /** @type {ContentController['start']} */
  async start() {
    await super.startCallback(async () => {
      const { contentControllers, layoutEl } = this;
      if (layoutEl) {
        Object.assign(layoutEl.style, { visibility: 'visible' });
      }

      await callBulkContentControllerMethod(
        contentControllers,
        (contentController) => contentController.start(0)
      );
    });
  }

  /** @type {ContentController['stop']} */
  async stop() {
    try {
      await super.stopCallback(async () => {
        const { parentEl, layoutEl, layerEls, contentControllers } = this;
        await callBulkContentControllerMethod(
          contentControllers,
          async (contentController) => {
            await contentController.stop();
            contentController.off('played', this.onPlayed);
            contentController.off('playing', this.onPlaying);
          }
        );
        if (!layoutEl) {
          return;
        }
        layerEls.forEach((el) => layoutEl.removeChild(el));
        parentEl.removeChild(layoutEl);
      });
    } catch {
      // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
      // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    }
  }
}

/**
 * @param {ContentController[]} contentControllers
 * @param {(contentController: ContentController) => Promise<void>} action
 * @returns {Promise<void>}
 */
async function callBulkContentControllerMethod(contentControllers, action) {
  await Promise.all(
    contentControllers.map((contentController) =>
      action(contentController).catch(() => {
        // Umyslné ignorování chyb.
      })
    )
  );
}
